import { Component } from '@theme/component';

/**
 * Drill-down mega menu for the liquid glass header.
 *
 * @extends {Component}
 */
class MegaMenuGlass extends Component {
  #submenuObserver;

  /** Locked panel min-height while drilling down so short views do not collapse under the cursor. */
  #lockedPanelMinHeight = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.#showCollections();
    this.#observeSubmenu();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#submenuObserver?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.#clearPanelHeightLock();
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const subcollectionsTrigger = event.target.closest('[data-mega-menu-glass-trigger="subcollections"]');
    if (subcollectionsTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#closeDrawerLocalization();
      const handle = subcollectionsTrigger.dataset.menuLinkHandle;
      if (handle) this.#showSubcollections(handle);
      return;
    }

    const collectionTrigger = event.target.closest('[data-mega-menu-glass-trigger="collection"]');
    if (collectionTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#closeDrawerLocalization();
      const handle = collectionTrigger.dataset.collectionHandle;
      if (handle) this.#showProducts(handle);
      return;
    }

    const backTrigger = event.target.closest('[data-mega-menu-glass-trigger="back"]');
    if (backTrigger) {
      event.preventDefault();
      this.#closeDrawerLocalization();
      this.#goBack();
    }
  };

  #closeDrawerLocalization() {
    const localization = this.closest('.menu-drawer')?.querySelector('menu-drawer-localization-glass-component');
    if (localization && 'hidePanel' in localization && typeof localization.hidePanel === 'function') {
      localization.hidePanel();
    }
  }

  /** Reset drill-down to the top collections list (used by menu-drawer-glass). */
  showCollections() {
    this.#showCollections();
  }

  #observeSubmenu() {
    const submenu = this.closest('.menu-list__submenu');
    if (!submenu) return;

    this.#submenuObserver = new MutationObserver(() => {
      if (!submenu.dataset.active) {
        this.#showCollections();
      }
    });

    this.#submenuObserver.observe(submenu, {
      attributes: true,
      attributeFilter: ['data-active'],
    });
  }

  #getVisibleView() {
    return this.querySelector('[data-mega-menu-glass-view]:not([hidden])');
  }

  #goBack() {
    const visibleView = this.#getVisibleView();
    if (!(visibleView instanceof HTMLElement)) {
      this.#showCollections();
      return;
    }

    const parentView = visibleView.dataset.parentView || 'collections';
    const parentHandle = visibleView.dataset.parentHandle || '';

    if (parentView === 'subcollections' && parentHandle) {
      this.#showSubcollections(parentHandle);
    } else {
      this.#showCollections();
    }
  }

  #hideAllDrilldownFooterLinks() {
    this.querySelectorAll('[data-mega-menu-glass-view-all-collection]').forEach((link) => {
      if (link instanceof HTMLElement) link.hidden = true;
    });
    this.querySelectorAll('[data-mega-menu-glass-view-all-subcollection]').forEach((link) => {
      if (link instanceof HTMLElement) link.hidden = true;
    });
  }

  #showCollections() {
    this.#clearPanelHeightLock();

    this.querySelectorAll('[data-mega-menu-glass-view]').forEach((view) => {
      if (!(view instanceof HTMLElement)) return;
      view.hidden = view.dataset.megaMenuGlassView !== 'collections';
    });

    this.querySelector('[data-mega-menu-glass-footer="collections"]')?.removeAttribute('hidden');
    this.querySelector('[data-mega-menu-glass-footer="products"]')?.setAttribute('hidden', '');
    this.#hideAllDrilldownFooterLinks();
    this.#updateSubmenuHeight();
  }

  /**
   * @param {string} menuLinkHandle
   */
  #showSubcollections(menuLinkHandle) {
    this.#lockPanelHeightFromCurrent();

    this.querySelectorAll('[data-mega-menu-glass-view]').forEach((view) => {
      if (!(view instanceof HTMLElement)) return;
      const isSubcollectionsView = view.dataset.megaMenuGlassView === 'subcollections';
      view.hidden = !isSubcollectionsView || view.dataset.menuLinkHandle !== menuLinkHandle;
    });

    this.querySelector('[data-mega-menu-glass-footer="collections"]')?.setAttribute('hidden', '');
    this.querySelector('[data-mega-menu-glass-footer="products"]')?.removeAttribute('hidden');
    this.#hideAllDrilldownFooterLinks();

    this.querySelectorAll('[data-mega-menu-glass-view-all-subcollection]').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;
      link.hidden = link.dataset.menuLinkHandle !== menuLinkHandle;
    });

    this.#updateSubmenuHeight();
    this.#focusDrawerBackButton();
    this.#scrollDrawerDrilldownIntoView();
  }

  /**
   * @param {string} handle
   */
  #showProducts(handle) {
    this.#lockPanelHeightFromCurrent();

    this.querySelectorAll('[data-mega-menu-glass-view]').forEach((view) => {
      if (!(view instanceof HTMLElement)) return;
      const isProductsView = view.dataset.megaMenuGlassView === 'products';
      view.hidden = !isProductsView || view.dataset.collectionHandle !== handle;
    });

    this.querySelector('[data-mega-menu-glass-footer="collections"]')?.setAttribute('hidden', '');
    this.querySelector('[data-mega-menu-glass-footer="products"]')?.removeAttribute('hidden');
    this.#hideAllDrilldownFooterLinks();

    this.querySelectorAll('[data-mega-menu-glass-view-all-collection]').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;
      link.hidden = link.dataset.collectionHandle !== handle;
    });

    this.#updateSubmenuHeight();
    this.#focusDrawerProductsFooter(handle);
    this.#scrollDrawerDrilldownIntoView();
  }

  /**
   * Keep desktop panel height when drilling into a shorter list so the cursor
   * stays over the submenu hit area (avoids accidental close on Club / sparse menus).
   */
  #lockPanelHeightFromCurrent() {
    if (this.closest('.menu-drawer--glass')) return;
    if (this.#lockedPanelMinHeight > 0) return;

    const panel = this.querySelector('.mega-menu-glass__panel');
    if (!(panel instanceof HTMLElement)) return;

    this.#lockedPanelMinHeight = Math.ceil(panel.getBoundingClientRect().height);
    if (this.#lockedPanelMinHeight > 0) {
      panel.style.minHeight = `${this.#lockedPanelMinHeight}px`;
      panel.classList.add('mega-menu-glass__panel--height-locked');
    }
  }

  #clearPanelHeightLock() {
    this.#lockedPanelMinHeight = 0;
    const panel = this.querySelector('.mega-menu-glass__panel');
    if (panel instanceof HTMLElement) {
      panel.style.minHeight = '';
      panel.classList.remove('mega-menu-glass__panel--height-locked');
    }
  }

  #scrollDrawerDrilldownIntoView() {
    if (!this.closest('.menu-drawer--glass')) return;

    requestAnimationFrame(() => {
      const visibleView = this.#getVisibleView();
      if (visibleView instanceof HTMLElement) {
        visibleView.scrollTop = 0;
      }
    });
  }

  #focusDrawerBackButton() {
    if (!this.closest('.menu-drawer--glass')) return;

    requestAnimationFrame(() => {
      const backButton = this.querySelector('[data-mega-menu-glass-trigger="back"]');
      if (backButton instanceof HTMLElement) {
        backButton.focus({ preventScroll: true });
      }
    });
  }

  /**
   * @param {string} handle
   */
  #focusDrawerProductsFooter(handle) {
    if (!this.closest('.menu-drawer--glass')) return;

    requestAnimationFrame(() => {
      const backButton = this.querySelector('[data-mega-menu-glass-trigger="back"]');
      const productList = this.querySelector(
        `.mega-menu-glass__view--products[data-collection-handle="${handle}"] .mega-menu-glass__list--scroll, .mega-menu-glass__view--products[data-collection-handle="${handle}"] .mega-menu-glass__columns--scroll`
      );

      productList?.scrollTo({ top: 0 });

      if (backButton instanceof HTMLElement) {
        backButton.focus({ preventScroll: true });
      }
    });
  }

  #updateSubmenuHeight() {
    const headerMenu = this.closest('header-menu');
    const submenu = this.closest('.menu-list__submenu');

    if (!(headerMenu instanceof HTMLElement) || !(submenu instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      headerMenu.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
    });
  }
}

if (!customElements.get('mega-menu-glass')) {
  customElements.define('mega-menu-glass', MegaMenuGlass);
}
